import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, List, ListItemButton,
  ListItemText, Grid, Paper, Divider, Avatar, Tabs, Tab, TextField, Button, Alert, Tooltip, IconButton,
} from '@mui/material';
import { Forum, Whatshot, Send, SupportAgent, SmartToy, Refresh } from '@mui/icons-material';
import { api } from '../config/api';
import { useAgentes } from '../context/AgenteContext';

const canalDe = (tel: string) =>
  tel?.startsWith('web:') ? 'Web' : tel?.startsWith('instagram:') ? 'Instagram' : tel?.startsWith('messenger:') ? 'Messenger' : 'WhatsApp';

const Conversaciones: React.FC = () => {
  const { agentes } = useAgentes();
  const variosAgentes = agentes.length > 1;
  const [tab, setTab] = useState(0);
  const [convs, setConvs] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [sel, setSel] = useState<any>(null);
  const [mensajes, setMensajes] = useState<any[]>([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const finRef = useRef<HTMLDivElement>(null);

  const cargarLista = useCallback(async () => {
    const r = await api.get('/dashboard/conversaciones');
    const lista = r.data.conversaciones || [];
    setConvs(lista);
    setSel((s: any) => (s ? lista.find((c: any) => c.id === s.id) || s : s));
  }, []);

  const cargarMensajes = useCallback(async (id: number) => {
    const { data } = await api.get(`/dashboard/conversaciones/${id}/mensajes`);
    setMensajes(data.mensajes || []);
  }, []);

  useEffect(() => {
    cargarLista();
    api.get('/dashboard/leads').then((r) => setLeads(r.data.leads || []));
  }, [cargarLista]);

  // Refresco automático mientras la página está abierta.
  useEffect(() => {
    const t = setInterval(() => {
      cargarLista().catch(() => {});
      if (sel) cargarMensajes(sel.id).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [sel, cargarLista, cargarMensajes]);

  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes.length]);

  const abrir = async (c: any) => {
    setSel(c); setError(''); setTexto('');
    await cargarMensajes(c.id);
  };

  const cambiarModo = async (accion: 'tomar' | 'devolver') => {
    if (!sel) return;
    setError('');
    try {
      const { data } = await api.post(`/dashboard/conversaciones/${sel.id}/${accion}`);
      setSel({ ...sel, modo: data.modo });
      cargarLista();
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo cambiar el modo');
    }
  };

  const responder = async () => {
    if (!sel || !texto.trim()) return;
    setEnviando(true); setError('');
    try {
      const { data } = await api.post(`/dashboard/conversaciones/${sel.id}/responder`, { mensaje: texto });
      setTexto('');
      setSel({ ...sel, modo: data.modo });
      await cargarMensajes(sel.id);
      cargarLista();
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo enviar el mensaje');
    } finally {
      setEnviando(false);
    }
  };

  const scoreColor = (s: number) => (s >= 85 ? 'error' : s >= 70 ? 'warning' : s >= 50 ? 'info' : 'default');
  const humano = sel?.modo === 'humano';
  const enEspera = convs.filter((c) => c.modo === 'humano').length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Forum color="primary" />
        <Typography variant="h4">Conversaciones y Leads</Typography>
        {enEspera > 0 && <Chip color="error" size="small" icon={<SupportAgent />} label={`${enEspera} con humano`} />}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Conversaciones (${convs.length})`} />
        <Tab label={`Leads capturados (${leads.length})`} />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card>
              <List sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {convs.map((c) => (
                  <ListItemButton key={c.id} selected={sel?.id === c.id} onClick={() => abrir(c)}>
                    <Avatar sx={{ bgcolor: c.modo === 'humano' ? 'error.main' : 'primary.main', mr: 2 }}>
                      {c.modo === 'humano' ? <SupportAgent fontSize="small" /> : (c.nombre_contacto || '?')[0]}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <span>{c.nombre_contacto || c.telefono}</span>
                          <Stack direction="row" spacing={0.5}>
                            {c.modo === 'humano' && <Chip size="small" color="error" label="Humano" />}
                            {c.es_lead && <Chip size="small" label={c.score_interes} color={scoreColor(c.score_interes)} />}
                          </Stack>
                        </Stack>
                      }
                      secondary={<Typography variant="body2" noWrap color="text.secondary">{canalDe(c.telefono)}{variosAgentes && c.agente_nombre ? ` · ${c.agente_nombre}` : ''} · {c.ultimo_mensaje}</Typography>}
                    />
                  </ListItemButton>
                ))}
                {convs.length === 0 && <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>Sin conversaciones aún</Typography>}
              </List>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {!sel ? (
                  <Typography color="text.secondary" textAlign="center" sx={{ mt: 10 }}>Selecciona una conversación</Typography>
                ) : (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                      <Box>
                        <Typography variant="h6">{sel.nombre_contacto || sel.telefono}</Typography>
                        <Typography variant="body2" color="text.secondary">{canalDe(sel.telefono)} · {sel.telefono}{variosAgentes && sel.agente_nombre ? ` · Agente: ${sel.agente_nombre}` : ''}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          icon={humano ? <SupportAgent /> : <SmartToy />}
                          color={humano ? 'error' : 'success'}
                          label={humano ? 'Atiende un humano' : 'Atiende el bot'}
                        />
                        {humano ? (
                          <Button size="small" variant="outlined" startIcon={<SmartToy />} onClick={() => cambiarModo('devolver')}>Devolver al bot</Button>
                        ) : (
                          <Button size="small" variant="outlined" color="error" startIcon={<SupportAgent />} onClick={() => cambiarModo('tomar')}>Tomar conversación</Button>
                        )}
                        <Tooltip title="Actualizar"><IconButton size="small" onClick={() => cargarMensajes(sel.id)}><Refresh fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={1} sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '50vh', pr: 1 }}>
                      {mensajes.map((m) => {
                        const entrante = m.direccion === 'entrante';
                        const deHumano = m.origen === 'agente';
                        return (
                          <Box key={m.id} sx={{ display: 'flex', justifyContent: entrante ? 'flex-start' : 'flex-end' }}>
                            <Paper sx={{ p: 1, px: 1.5, maxWidth: '75%', bgcolor: entrante ? '#fff' : deHumano ? '#E0F2FE' : '#DCF8C6' }}>
                              {!entrante && (
                                <Typography variant="caption" sx={{ fontWeight: 700, color: deHumano ? '#0369A1' : '#128C7E' }}>
                                  {deHumano ? 'Tú' : 'Bot'}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.mensaje}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
                                {new Date(m.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                              </Typography>
                            </Paper>
                          </Box>
                        );
                      })}
                      <div ref={finRef} />
                    </Stack>
                    {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <TextField
                        fullWidth size="small" multiline maxRows={4}
                        placeholder={humano ? 'Escribe tu respuesta...' : 'Escribe para responder tú (el bot se pausa en esta conversación)'}
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); responder(); } }}
                      />
                      <Button variant="contained" endIcon={<Send />} onClick={responder} disabled={enviando || !texto.trim()}>
                        {enviando ? 'Enviando' : 'Enviar'}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Al responder tú, el bot deja de contestar en esta conversación hasta que la devuelvas.
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          {leads.map((l) => (
            <Grid item xs={12} sm={6} md={4} key={l.id}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Whatshot color="warning" fontSize="small" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{l.nombre || l.telefono}</Typography>
                    </Stack>
                    <Chip size="small" label={`${l.score}`} color={scoreColor(l.score)} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">📱 {l.telefono}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>{l.interes}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {leads.length === 0 && <Grid item xs={12}><Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>Sin leads capturados aún</Typography></Grid>}
        </Grid>
      )}
    </Box>
  );
};

export default Conversaciones;
